import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { RunningTask, useExpansionList, useTaskStopRequest } from "hooks";
import moment from "moment";
import { CustomButton, CustomTag } from "core";
import { getExpansionName } from "modules/expansions/business";
import {
  getTaskStatusColor,
  getTaskStatusName,
} from "modules/shared/options/TaskStatusOption";
import { getTaskTypeName } from "modules/shared/options/TaskTypeOption";
import useStyles from "./styles";

type RunningTaskBannerProps = {
  runningTask: RunningTask | null;
  isForeignExpansion: boolean;
};

const formatElapsed = (startTime: string) => {
  const seconds = Math.max(0, moment().diff(moment(startTime), "seconds"));
  const minutes = Math.floor(seconds / 60);

  return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
};

export const RunningTaskBanner: React.FC<RunningTaskBannerProps> = ({
  runningTask,
  isForeignExpansion,
}) => {
  const { classes, cx } = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const { expansionList } = useExpansionList(false);

  const { requestStop, isRequesting, isStopRequested } = useTaskStopRequest();

  // Die tickende Uhr lebt hier und nicht im Hook: sonst würde jeder Consumer von
  // useRunningTask einmal pro Sekunde neu rendern.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!runningTask) {
      return;
    }

    const interval = setInterval(() => setTick((prev) => prev + 1), 1000);

    return () => clearInterval(interval);
  }, [runningTask]);

  if (!runningTask) {
    return null;
  }

  const isUnresponsive = runningTask.isStale || runningTask.isStuckPending;

  const stopRequested = isStopRequested(
    runningTask.taskID,
    runningTask.stopRequestedAt,
  );

  const expansion = expansionList.find(
    (item) => item.id === runningTask.expansionID,
  );

  const expansionName = expansion
    ? getExpansionName(expansion)
    : "einer anderen Edition";

  const stopHandler = async () => {
    try {
      // Bei einer nicht mehr reagierenden Aufgabe hilft der kooperative Stopp
      // nicht - dort pollt niemand mehr das Feld. Diese Zeile wird direkt
      // terminalisiert, damit die Sperre wieder freigegeben werden kann.
      await requestStop({
        taskType: runningTask.taskType,
        taskID: runningTask.taskID,
        forceRelease: isUnresponsive,
      });

      enqueueSnackbar(
        isUnresponsive
          ? "Die Aufgabe wurde freigegeben. Du kannst jetzt eine neue starten."
          : "Stopp wurde angefordert. Die Aufgabe wird beendet, sobald die laufende Karte fertig ist - das kann bis zu einer Minute dauern.",
        { variant: "info" },
      );
    } catch (error) {
      enqueueSnackbar(
        `Stopp konnte nicht angefordert werden: ${
          error instanceof Error ? error.message : "unbekannter Fehler"
        }`,
        { variant: "error" },
      );
    }
  };

  const getButtonText = () => {
    if (isRequesting) {
      return isUnresponsive ? "Wird freigegeben …" : "Stopp wird angefordert …";
    }

    if (isUnresponsive) {
      return "Aufgabe freigeben";
    }

    return stopRequested
      ? "Stopp angefordert – läuft aus …"
      : "Aufgabe stoppen";
  };

  const getMessage = () => {
    if (runningTask.isStuckPending) {
      return `${getTaskTypeName(runningTask.taskType)} wurde vor ${formatElapsed(runningTask.startTime)} erstellt, aber nicht angenommen.`;
    }

    if (runningTask.isStale) {
      return `${getTaskTypeName(runningTask.taskType)} blockiert das System seit ${formatElapsed(runningTask.startTime)}, reagiert aber nicht mehr.`;
    }

    if (isForeignExpansion) {
      return `${getTaskTypeName(runningTask.taskType)} für Edition »${expansionName}« läuft seit ${formatElapsed(runningTask.startTime)}. Neue Aufgaben können erst gestartet werden, wenn diese beendet ist.`;
    }

    return `${getTaskTypeName(runningTask.taskType)} läuft seit ${formatElapsed(runningTask.startTime)}.`;
  };

  return (
    <div className={cx(classes.banner, isUnresponsive && classes.bannerStale)}>
      <CustomTag
        content={getTaskStatusName(runningTask.status)}
        tagColor={getTaskStatusColor(runningTask.status)}
        tagSize="small"
      />

      <Typography variant="body2" className={classes.text}>
        {getMessage()}
        {stopRequested && !isUnresponsive && (
          <span className={classes.hint}>
            {" "}
            Bereits berechnete Preise werden noch zu Cardmarket übertragen.
          </span>
        )}
      </Typography>

      <CustomButton
        text={getButtonText()}
        size="xsmall"
        color={isUnresponsive ? "grey" : "red"}
        onClick={stopHandler}
        // loading allein deaktiviert den Button in dieser Komponente nicht -
        // disabled muss explizit mit, sonst ist ein Doppelklick möglich.
        loading={isRequesting || (stopRequested && !isUnresponsive)}
        loadingTime={0}
        disabled={isRequesting || (stopRequested && !isUnresponsive)}
      />
    </div>
  );
};
